import { MutableRefObject } from 'react';

interface IKeyChangeFocusProps {
    event: any;
    itemsRef: MutableRefObject<any[]>;
    currentItemIndex: number;
}

interface IKeySubmitProps {
    key: string;
    element: any;
}

export function keyTabChangeFocusByID({
    event,
    elements,
    nextElementId,
}: {
    event: KeyboardEvent;
    elements: string[];
    nextElementId?: string;
}) {
    const isTabKey = event.keyCode === 9;
    const currentItem = document?.activeElement?.id;
    const hasElementOfListFocused = currentItem && elements?.find((item: string) => item === currentItem);

    if (!isTabKey || !elements?.length || !hasElementOfListFocused) {
        return;
    }

    const currentItemIndex = elements.findIndex((item: string) => item === currentItem);
    const hasLastItemFocused = currentItemIndex === elements.length - 1;

    if (hasLastItemFocused && !nextElementId) {
        document.getElementById(elements[0])?.focus();

        event.preventDefault();

        return;
    }

    if (hasLastItemFocused && nextElementId) {
        document.getElementById(nextElementId)?.focus();

        event.preventDefault();

        return;
    }

    document.getElementById(elements[currentItemIndex + 1])?.focus();
    event.preventDefault();
    return;
}

export const keyDownChangeFocus = ({ event, itemsRef, currentItemIndex }: IKeyChangeFocusProps) => {
    if (!itemsRef?.current?.length) return;
    const isActionSwitchFocusDown = event.keyCode === 13 || event.which === 40;
    const isActionSwitchFocusUp = event.which === 38;
    const isLastInputFocusing = currentItemIndex === itemsRef?.current.length - 1;
    const isFirstInputFocusing = currentItemIndex === 0;
    const isSwitchFocusKey = event.keyCode === 13 || event.which === 40 || event.which === 38;

    if (isActionSwitchFocusDown && !isLastInputFocusing) {
        itemsRef?.current[currentItemIndex].blur();
        itemsRef?.current[currentItemIndex + 1]?.focus();

        event.preventDefault();
        return;
    }

    if (isActionSwitchFocusUp && !isFirstInputFocusing) {
        itemsRef?.current[currentItemIndex].blur();
        itemsRef?.current[currentItemIndex - 1]?.focus();

        event.preventDefault();
        return;
    }

    if (isSwitchFocusKey && (isLastInputFocusing || isFirstInputFocusing)) {
        event.preventDefault();
        return;
    }

    return;
};

export const keyEnterDownChangeFocus = ({ event, itemsRef, currentItemIndex }: IKeyChangeFocusProps) => {
    if (!itemsRef?.current?.length) return;
    const isEnterKey = event.keyCode === 13;
    const isLastInputFocusing = currentItemIndex === itemsRef?.current?.length - 1;

    if (isEnterKey && !isLastInputFocusing) {
        itemsRef?.current[currentItemIndex]?.blur();
        itemsRef?.current[currentItemIndex + 1]?.focus();

        event.preventDefault();
        return;
    }

    return;
};

export const keyEnterDownChangeFocusByID = ({
    event,
    itemIDs,
    currentItemIndex,
}: {
    event: any;
    itemIDs: string[];
    currentItemIndex: number;
}) => {
    if (!itemIDs?.length) return;
    const isEnterKey = event.keyCode === 13;
    const isLastInputFocusing = currentItemIndex === itemIDs?.length - 1;
    const isNextElementInput =
        document.getElementById(itemIDs[currentItemIndex + 1])?.tagName?.toLocaleUpperCase() === 'INPUT';

    if (!isEnterKey || isLastInputFocusing) return;

    if (isEnterKey && isNextElementInput) {
        document.getElementById(itemIDs[currentItemIndex])?.blur();
        document.getElementById(itemIDs[currentItemIndex + 1])?.focus();

        event.preventDefault();
        return;
    }

    if (isEnterKey && !isNextElementInput) {
        keyEnterDownChangeFocusByID({
            event,
            itemIDs,
            currentItemIndex: currentItemIndex + 1,
        });
    }

    return;
};

export const keyEnterSubmit = ({ key, element }: IKeySubmitProps) => {
    const isKeyEnter = key === 'Enter';
    if (!element || !isKeyEnter) return;

    element.click();
};

export function keyRowsChangeFocus({
    event,
    rowRefs,
}: {
    event: KeyboardEvent;
    rowRefs: MutableRefObject<any[] | null>;
}) {
    const activeElement = document.activeElement;
    const activeRowId = activeElement?.getAttribute('data-row-key');
    const currentRowFocusingIndex = rowRefs?.current?.findIndex(
        (row: any) => row?.getAttribute('data-row-key') === activeRowId,
    );

    if (!rowRefs?.current?.length || currentRowFocusingIndex === undefined || currentRowFocusingIndex < 0) return;

    return keyDownChangeFocus({
        event,
        itemsRef: rowRefs as MutableRefObject<any[]>,
        currentItemIndex: currentRowFocusingIndex || 0,
    });
}

export const keyRowCellChangeFocus = ({
    event,
    data,
    cellsCount,
    setFocusRowId,
    elementWillBeFocusAfterTableId,
    rowKey = 'id',
}: {
    event: KeyboardEvent;
    data: any[];
    cellsCount: number;
    setFocusRowId?: (rowId: string) => void;
    elementWillBeFocusAfterTableId?: string;
    rowKey?: string;
}) => {
    const activeElement = document.activeElement;
    const isFocusLeftKey = event.which === 37;
    const isFocusRightKey = event.which === 39;
    const isTabKey = event.keyCode === 9;
    const isSwitchFocusDown = event.keyCode === 13 || event.which === 40;
    const isSwitchFocusUp = event.which === 38;
    let cellIds: string[] = [];
    let currentCellIdFocusing = null;
    let currentCellIndex = null;

    if (!data?.length) return;

    const keyCells: number[] = Array(cellsCount)
        .fill('')
        .map((_v, i) => i);

    data.forEach((row, _idx) => {
        keyCells.forEach((cellNumber: number) => {
            cellIds = [...cellIds, `td.${row?.[rowKey]?.toString()}.${cellNumber}`];
        });
    });

    const activeRowId = activeElement?.getAttribute('data-row-key');

    if (!!activeRowId && isTabKey) {
        setFocusRowId && setFocusRowId(activeRowId);
        document.getElementById(`td.${activeRowId}.1`)?.focus();

        event.preventDefault();
        return;
    }

    const focusingId = activeElement?.id || '';
    const hasCellFocusing = cellIds?.includes(focusingId);
    const isLastTableCell = hasCellFocusing && focusingId === cellIds[cellIds?.length - 1];
    const isFirstTableCell = hasCellFocusing && focusingId === cellIds[0];

    if (!hasCellFocusing) {
        return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    currentCellIdFocusing = focusingId;
    currentCellIndex = cellIds.findIndex((cellId: string) => cellId === focusingId);

    if (isFocusLeftKey && !isFirstTableCell && currentCellIndex !== null) {
        const rowId = cellIds[currentCellIndex - 1].split('.')[1];
        setFocusRowId && setFocusRowId(rowId);
        document.getElementById(cellIds[currentCellIndex - 1])?.focus();

        event.preventDefault();
        return;
    }

    if ((isTabKey || isFocusRightKey) && !isLastTableCell && currentCellIndex !== null) {
        const rowId = cellIds[currentCellIndex + 1].split('.')[1];
        setFocusRowId && setFocusRowId(rowId);
        document.getElementById(cellIds[currentCellIndex + 1])?.focus();

        event.preventDefault();
        return;
    }

    if (isTabKey && isLastTableCell && currentCellIndex !== null && elementWillBeFocusAfterTableId) {
        document.getElementById(elementWillBeFocusAfterTableId)?.focus();

        event.preventDefault();
        return;
    }

    if ((isFocusLeftKey && isFirstTableCell) || (isFocusRightKey && isLastTableCell)) {
        event.preventDefault();
        return;
    }

    const nextCellRow =
        currentCellIndex !== null &&
        currentCellIndex + cellsCount <= cellIds?.length - 1 &&
        cellIds[currentCellIndex + cellsCount];

    if (isSwitchFocusDown && nextCellRow) {
        const rowId = nextCellRow.split('.')[1];
        setFocusRowId && setFocusRowId(rowId);

        document.getElementById(nextCellRow)?.focus();

        event.preventDefault();
        return;
    }

    const prevCellRow =
        currentCellIndex !== null && currentCellIndex - cellsCount >= 0 && cellIds[currentCellIndex - cellsCount];

    if (isSwitchFocusUp && prevCellRow) {
        const rowId = prevCellRow.split('.')[1];
        setFocusRowId && setFocusRowId(rowId);
        document.getElementById(prevCellRow)?.focus();

        event.preventDefault();
        return;
    }

    return;
};

// for pagination of antd
export const keyPaginationTab = ({
    event,
    nextElementId,
    currentPage,
    totalCount,
    pageSize,
}: {
    event: KeyboardEvent;
    nextElementId: string;
    currentPage: number;
    totalCount: number;
    pageSize: number;
}) => {
    const pagesCount = Math.ceil(totalCount / pageSize);
    const activeElement = document.activeElement;

    const elementFocusedTitle = activeElement?.getAttribute('title');
    const paginationItemFocused = Number(elementFocusedTitle || 0);
    const hasPaginationActiveFocused = paginationItemFocused > 0 && paginationItemFocused + 1 === currentPage;

    if (
        (paginationItemFocused === 1 && (totalCount === 0 || pagesCount === 1)) ||
        (hasPaginationActiveFocused && pagesCount === currentPage) ||
        elementFocusedTitle === 'Next Page'
    ) {
        document.getElementById(nextElementId)?.focus();

        event.preventDefault();
        return;
    }

    return;
};
